import React from 'react';
import { motion } from 'framer-motion';
import { 
  SparklesIcon,
  TrophyIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const EliteStatsSection = () => {
  const stats = [
    {
      icon: TrophyIcon,
      value: '30%',
      label: 'Profit Reinvestment',
      description: 'of returns automatically reinvested'
    },
    {
      icon: ClockIcon,
      value: '50%',
      label: 'Project Profits',
      description: 'immediate return on investment'
    },
    {
      icon: UserGroupIcon,
      value: '10K',
      label: 'Elite Members',
      description: 'exclusive NFT collection limit'
    },
    {
      icon: SparklesIcon,
      value: '1st',
      label: 'Market Leader',
      description: 'only firm with this profit model'
    }
  ];

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-gray-900 via-amber-900/10 to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-600/5 to-blue-600/5"></div>
        {/* Animated background particles */}
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-amber-400 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -15, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 lg:mb-6 bg-gradient-to-r from-yellow-400 via-blue-400 to-amber-500 bg-clip-text text-transparent leading-tight"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Join the Elite
          </motion.h2>
          
          <motion.div
            className="max-w-5xl mx-auto mb-6 lg:mb-8 px-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <p className="text-xl sm:text-2xl md:text-3xl text-gray-200 font-semibold mb-4">
              🚀 Don't Miss This Exclusive Opportunity!
            </p>
            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-4">
              <span className="text-amber-400 font-bold">OCTAGON INFINITY</span> is the{' '}
              <span className="text-blue-400 font-semibold">only investment firm in the market</span>{' '}
              offering <span className="text-green-400 font-bold">80% total profit to holders</span> and{' '}
              <span className="text-yellow-400 font-bold">50% initial payments</span> to participants.
            </p>
            <p className="text-base sm:text-lg text-amber-300 font-medium mb-3">
              ⏰ Limited to 10,000 NFTs • Join before it's too late!
            </p>
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12 lg:mb-16 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-gray-800/50 backdrop-blur-lg rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-gray-700 hover:border-amber-500 transition-all duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 + 0.8 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <stat.icon className="w-8 h-8 lg:w-12 lg:h-12 text-amber-400 mx-auto mb-3 lg:mb-4" />
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1 lg:mb-2">{stat.value}</div>
              <div className="text-sm lg:text-lg font-semibold text-amber-300 mb-1 lg:mb-2">{stat.label}</div>
              <div className="text-xs lg:text-sm text-gray-400">{stat.description}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default EliteStatsSection;